import { Ref } from 'vue'

import { LineupPlayer } from '@collector/desktop-feature-lineups'
import { sportsApiClient } from '@desktop/globalState/sportsApiClient'
import { queryClient } from '@desktop/queryClient'
import { useEventId } from '@desktop/views/Relation/composables/useEventId'
import { getSubParticipantsPostprocessor } from '@desktop/views/Relation/utils/subParticipantsPostprocessor'
import {
  queryOptions,
  useQuery,
  type UndefinedInitialQueryOptionsWithDataTag,
  type UseQueryReturnType,
} from '@tanstack/vue-query'

import { competitionQueryOptions } from './useCompetitionQuery'

type SubParticipantsQueryKey = readonly [
  'event',
  'subParticipants',
  Ref<number>,
]

type SubParticipantsQueryOptions = UndefinedInitialQueryOptionsWithDataTag<
  LineupPlayer[],
  Error,
  LineupPlayer[],
  SubParticipantsQueryKey
>

export function subParticipantsQueryOptions(
  eventId: Ref<number>,
): SubParticipantsQueryOptions {
  return queryOptions({
    queryKey: ['event', 'subParticipants', eventId] as const,
    queryFn: async (): Promise<LineupPlayer[]> => {
      const [response, competitionData] = await Promise.all([
        sportsApiClient.Events.SubParticipants.get(eventId.value),
        queryClient.ensureQueryData(competitionQueryOptions(eventId)),
      ])

      const players = response.api.data.sub_participants
      const postprocess = getSubParticipantsPostprocessor(
        competitionData.competition.sport_id,
      )

      if (!postprocess) {
        return players
      }

      return postprocess(eventId.value, players)
    },
  })
}

export const useSubParticipantsQuery = (
  options?: Omit<SubParticipantsQueryOptions, 'queryKey' | 'queryFn'>,
): UseQueryReturnType<LineupPlayer[], Error> => {
  const eventId = useEventId()
  return useQuery<
    LineupPlayer[],
    Error,
    LineupPlayer[],
    SubParticipantsQueryKey
  >({
    ...subParticipantsQueryOptions(eventId),
    ...options,
  })
}
